import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import StatTile from '../ui/StatTile';
import { API_BASE_URL } from '../../config';
import { RiStore2Line, RiCheckboxCircleLine, RiCloseCircleLine, RiFilterOffLine } from 'react-icons/ri';
import { _t, _n } from '../../utils/i18n';

const DNProductStoresDetail = ({ selectedProject, selectedProducts, fetchWithAuth, startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [storesData, setStoresData] = useState({ stats: {}, stores: [] });

  // Fetch data for the selected product
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedProject?.value || !selectedProducts || selectedProducts.length === 0) return;

      try {
        setLoading(true);

        // Si plus d'un produit sélectionné, on ne prend que le premier
        const productName = selectedProducts[0].value;
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const url = `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/products/details/${encodeURIComponent(productName)}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setStoresData(data);
        } else {
          console.error('Failed to fetch product stores data');
        }
      } catch (error) {
        console.error("Error fetching product stores data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedProject, selectedProducts, startDate, endDate, fetchWithAuth]);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  // Date sorting function
  const dateSorter = (a, b, direction) => {
    try {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (isNaN(dateA.getTime())) return direction === 'ascending' ? -1 : 1;
      if (isNaN(dateB.getTime())) return direction === 'ascending' ? 1 : -1;

      if (dateA < dateB) return direction === 'ascending' ? -1 : 1;
      if (dateA > dateB) return direction === 'ascending' ? 1 : -1;
      return 0;
    } catch (e) {
      return 0;
    }
  };

  // Define columns for the table
  const columns = [
    {
      key: 'interventionPoint',
      label: _t('Intervention point'),
      filterable: true,
    },
    {
      key: 'shopSign',
      label: _t('Shop sign'),
      filterable: true,
    },
    {
      key: 'isPresent',
      label: _t('Status'),
      rawValue: (item) => {
        if (item.isPresent) {
          return _t('Present');
        } else if (item.reasonCategory === 'Not in Store Range') {
          return _t('Not in Store Range');
        } else {
          return _t('Absent');
        }
      },
      render: (item) => {
        if (item.isPresent) {
          return <span className="flex items-center text-green-600"><RiCheckboxCircleLine className="mr-1" /> {_t('Present')}</span>;
        } else if (item.reasonCategory === 'Not in Store Range') {
          return <span className="flex items-center text-gray-500"><RiFilterOffLine className="mr-1" /> {_t('Not in Store Range')}</span>;
        }

        return <span className="flex items-center text-red-600"><RiCloseCircleLine className="mr-1" /> {_t('Absent')}</span>;
      },
      sortFunction: (a, b, direction) => {
        // Special case: "Not in Store Range" should always be last
        if (a.reasonCategory === 'Not in Store Range' && b.reasonCategory !== 'Not in Store Range') {
          return 1; // a goes after b
        }
        if (a.reasonCategory !== 'Not in Store Range' && b.reasonCategory === 'Not in Store Range') {
          return -1; // a goes before b
        }

        // For other cases, use normal sorting logic
        // Get status category: 2 for present, 0 for absent
        const getStatusValue = (item) => {
          if (item.isPresent) return 2;
          return 0;
        };

        const valueA = getStatusValue(a);
        const valueB = getStatusValue(b);

        if (direction === 'ascending') {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      },
      filterable: true,
    },
    {
      key: 'reason',
      label: _t('Reason'),
      filterable: true,
    },
    {
      key: 'date',
      label: _t('Last visit date'),
      filterable: false,
      sortFunction: dateSorter,
      render: (row, isRawValue) => isRawValue ? row.date : formatDate(row.date)
    },
    {
      key: 'daysInState',
      label: _t('Days in state'),
      render: (item, isRawValue) => isRawValue ? item.daysInState : `${item.daysInState} jour${item.daysInState > 1 ? 's' : ''}`
    }
  ];

  const { stats } = storesData;

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        <div className="grid grid-cols-4 gap-4">
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (!selectedProducts || selectedProducts.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500 dark:text-gray-400">{_t('Please select a product to display the details')}</p>
      </div>
    );
  }

  if (selectedProducts.length > 1) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500 dark:text-gray-400">{_t('To display store details, please select only one product')}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500 dark:text-gray-400">{_t('No data available for this product')}</p>
      </div>
    );
  }

  const getDNColorClass = (rate) => {
    if (rate >= 90) return "blue";
    if (rate >= 75) return "green";
    if (rate >= 50) return "amber";
    if (rate >= 25) return "orange";
    return "red";
  };

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile
            label={_t('Total stores')}
            value={stats.totalStores.toString()}
            icon={<RiStore2Line />}
            color="blue"
            size="s"
          />
          <StatTile
            label={_t('Stores with product')}
            value={stats.presentStores.toString()}
            icon={<RiCheckboxCircleLine />}
            color="green"
            size="s"
          />
          <StatTile
            label={_t('Stores without product')}
            value={(stats.notPresentStores).toString()}
            icon={<RiCloseCircleLine />}
            color="red"
            size="s"
          />
          <StatTile
            label={_t('DN rate (%)')}
            value={`${stats.dnRate.toFixed(1)}%`}
            icon={<RiStore2Line />}
            color={getDNColorClass(parseFloat(stats.dnRate))}
            size="s"
          />
        </div>
      )}

      <SortableTable
        data={storesData.stores || []}
        columns={columns}
        emptyMessage={_t('No data available for this product')}
        countLabel={`${storesData.stores?.length || 0} ${_n('store', 'stores', storesData.stores?.length)}`}
        initialSortBy={{ key: 'isPresent', direction: 'descending' }}
        exportable={true}
        exportFilename={`details_magasins_${selectedProducts[0]?.value || 'produit'}.csv`}
      />
    </div>
  );
};

export default DNProductStoresDetail;
